import { type User, WebStorageStateStore } from 'oidc-client-ts';
import React from 'react';
import { AuthProvider, useAuth } from 'react-oidc-context';

import { listenTokenExpired } from '@shared-legacy/services/appstore/api';
import { logAutoSignInRedirect, logLogoutRaceEvent, shouldSuppressAutoSignIn } from '@shared/lib/auth/logoutGuard';
import { getSigninRedirectUri } from '@shared/lib/auth/signinRedirectUri';

const SignInOnExpiredToken = () => {
  const { events, isAuthenticated, signinRedirect, signinSilent } = useAuth();
  React.useEffect(() => {
    if (!isAuthenticated) return undefined;
    // sign-out on expired token detected on the client...
    const removeAccessTokenExpired = events.addAccessTokenExpired(() => {
      logLogoutRaceEvent('addAccessTokenExpired fired');
      if (shouldSuppressAutoSignIn('ProviderAppAuth:access-token-expired')) return;
      logAutoSignInRedirect('ProviderAppAuth:access-token-expired');
      void signinRedirect();
    });
    const removeTokenExpiredListener = listenTokenExpired(() => {
      logLogoutRaceEvent('listenTokenExpired fired (server 401 / invalid bearer)');
      // ...or if token is expired on the server
      signinSilent().catch(() => {
        logLogoutRaceEvent('signinSilent failed after server token expiry');
        if (shouldSuppressAutoSignIn('ProviderAppAuth:server-token-expired')) return;
        logAutoSignInRedirect('ProviderAppAuth:server-token-expired');
        void signinRedirect();
      });
    });
    return () => {
      removeAccessTokenExpired();
      removeTokenExpiredListener();
    };
  }, [events, isAuthenticated, signinRedirect, signinSilent]);
  return null;
};

const onSigninCallback = (_user: User | void): void => {
  window.history.replaceState({}, document.title, `${window.location.pathname}${window.location.hash}`);
};

const userStore = new WebStorageStateStore({ store: window.localStorage });

export type ProviderAppAuthProps = {
  authority: string;
  children: React.ReactNode;
  client_id: string;
};

export const ProviderAppAuth = (props: ProviderAppAuthProps) => {
  return (
    <AuthProvider
      authority={props.authority}
      client_id={props.client_id}
      includeIdTokenInSilentSignout={true}
      onSigninCallback={onSigninCallback}
      post_logout_redirect_uri={`${window.location.origin}/logout`}
      redirect_uri={getSigninRedirectUri()}
      revokeTokensOnSignout={false}
      userStore={userStore}
    >
      <SignInOnExpiredToken />
      {props.children}
    </AuthProvider>
  );
};
